import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { ConnectionState } from '../realtime/useRealtime'
import type { DashboardView, DaySchedule, Project, ScheduleRow } from './types'
import { formatShortDate, formatTime, withAlpha } from '../lib/format'
import { progressColor } from '../lib/progressColor'
import { COPYRIGHT_TEXT } from '../components/Footer'
import { Avatar } from '../components/ui'
import { TvMap } from './TvMap'

/**
 * Selve TV-visningen: optimeret til 1920x1080 og aflæsning på afstand
 * (kravspec afsnit 14-16). Farverne kommer fra statusrækkerne i databasen,
 * så adminpanelet styrer udseendet uden en ny frontend-build.
 */
export function TvDashboard({
  view,
  title,
  screenName,
  connection,
  isOffline,
  isPreview = false
}: {
  view: DashboardView | null
  title: string
  screenName: string | null
  connection: ConnectionState
  isOffline: boolean
  /** Forhåndsvisning fra Dashboards: ingen realtid, så forbindelseslinjen ville kun forvirre. */
  isPreview?: boolean
}) {
  const clock = useClock()

  return (
    <div className="tv">
      <header className="tv__header">
        {/* Logoet til mørke baggrunde: hvid tekst med sort kant, så det står direkte
            på TV-baggrunden uden den hvide plade, det gamle logo krævede. */}
        <div className="tv__brand">
          <img src="/logo/tc-logo-dark.png" alt="theodor christiansen" />
        </div>
        <div>
          <div className="tv__title">{title.toUpperCase()}</div>
          {screenName && <div className="tv__subtitle">{screenName}</div>}
        </div>
        {/* Foedselsdage og jubilaeer i dag (beslutning 117). I toppen, ikke i sidefoden: en lang sagstabel
            skubber sidefoden ud af skaermen. */}
        {view?.celebrations && view.celebrations.length > 0 && (
          <div className="tv__celebration">
            {view.celebrations
              .map((c) =>
                c.kind === 'birthday'
                  ? `🎂 Tillykke til ${c.user.displayName}`
                  : `🎉 ${c.user.displayName} har ${c.years} års jubilæum`
              )
              .join('   ·   ')}
          </div>
        )}
        {/* Tilkaldevagten (beslutning 120) - i toppen af samme grund som foedselsdagene. */}
        {view?.onCall?.current?.user && (
          <div className="tv__oncall">
            📟 Vagt: <strong>{view.onCall.current.user.displayName}</strong>
            {view.onCall.current.user.phoneNumber && <span> · {view.onCall.current.user.phoneNumber}</span>}
          </div>
        )}
        <div className="tv__clock">{clock}</div>
      </header>

      {view?.statistics && (
        <div className="tv__stats">
          <Stat value={view.statistics.activeProjects} label="Aktive projekter" />
          <Stat value={view.statistics.waitingProjects} label="Afventer" color="#F97316" />
          <Stat value={view.statistics.overdueProjects} label="Forsinkede" color="#EF4444" />
          <Stat value={view.statistics.completedThisMonth} label="Afsluttet i md." color="#22C55E" />
          <Stat value={view.statistics.activeTasks} label="Aktive opgaver" />
          <Stat value={view.statistics.tasksDueThisWeek} label="Deadline i uge" color="#EAB308" />
        </div>
      )}

      {/* Ugens faglige tip (beslutning 121) - under tallene. Tabellen maaler selv, hvor meget plads der er tilbage. */}
      {view?.tip && (
        <div className="tv__tip">
          <span className="tv__tip-label">💡 Ugens tip</span>
          <strong>{view.tip.title}</strong>
          <span className="tv__tip-body">{view.tip.body}</span>
        </div>
      )}

      {/* "Ugen der gik" (beslutning 126) - fredag fra kl. 14. Kun tal og det faelles, aldrig hvem der har ferie. */}
      {view?.weekSummary && <WeekSummaryStrip summary={view.weekSummary} />}

      {view?.staffing ? (
        <div className="tv__split">
          {/* En tabel kan ikke selv skære det af, der ikke er plads til - det gør boksen. */}
          <div className="tv__split-main">
            {view.map ? <TvMap map={view.map} /> : <ProjectsTable view={view} compact />}
          </div>
          <StaffingPanel schedule={view.staffing} />
        </div>
      ) : view?.map ? (
        // Kortet i stedet for tabellen (beslutning 111).
        <TvMap map={view.map} />
      ) : (
        <ProjectsTable view={view} />
      )}

      <footer className="tv__footer">
        {isPreview ? (
          <span>Forhåndsvisning - sådan ser skærmen ud</span>
        ) : (
          <>
            <span
              className="tv__status-dot"
              style={{ background: isOffline ? '#EF4444' : connection === 'connected' ? '#22C55E' : '#EAB308' }}
            />
            <span>
              {isOffline
                ? 'Ingen forbindelse til serveren'
                : connection === 'connected'
                  ? 'Live - opdateres automatisk'
                  : 'Genopretter forbindelse ...'}
            </span>
          </>
        )}
        <span style={{ marginLeft: 'auto', opacity: 0.7 }}>{COPYRIGHT_TEXT}</span>
        {view && (
          <span>
            Opdateret {new Date(view.generatedAt).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </footer>
    </div>
  )
}

/**
 * Sagerne. Står dagens bemanding ved siden af, er der kun plads til det vigtigste:
 * projektleder og næste handling udelades, så resten stadig kan læses på afstand.
 */
/** Hoejden paa linjen "+ N flere sager" (38 px maalt i browseren) med lidt luft. */
const MORE_ROW_HEIGHT = 44

function ProjectsTable({ view, compact = false }: { view: DashboardView | null; compact?: boolean }) {
  const columns = compact ? 4 : 6
  const projects = view?.projects ?? []

  // Kun de raekker, der kan vaere paa skaermen - resten bliver til "+ N flere sager". Uden det skubbede en lang
  // tabel sidefoden (og "Live") ud af skaermen, og den nederste raekke blev klippet midt over.
  //
  // Maalingen sker i to trin: alle raekker tegnes (fit = null), deres placering maales, og saa skaeres der til.
  // Skifter sagerne eller skaermens stoerrelse, maales der forfra.
  const box = useRef<HTMLDivElement>(null)
  const [fit, setFit] = useState<number | null>(null)
  const [height, setHeight] = useState(0)
  const key = projects.map((p) => p.id).join(',')

  useEffect(() => {
    const element = box.current
    if (!element) return
    const measure = () => setHeight(element.clientHeight)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    // Ogsaa vinduets resize: ResizeObserver melder kun, naar siden tegnes, og en skaerm, der skifter
    // oploesning uden at tegne, skal stadig maale forfra.
    window.addEventListener('resize', measure)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])

  useLayoutEffect(() => {
    setFit(null)
  }, [key, height, compact])

  useLayoutEffect(() => {
    if (fit !== null) return
    const element = box.current
    if (!element) return

    const rows = Array.from(element.querySelectorAll<HTMLTableRowElement>('tbody tr[data-project]'))
    const bottom = element.getBoundingClientRect().bottom
    const fitsAll = rows.every((row) => row.getBoundingClientRect().bottom <= bottom + 1)

    // Er der ikke plads til dem alle, skal linjen "+ N flere" ogsaa kunne staa der - den er MORE_ROW_HEIGHT hoej.
    setFit(
      fitsAll
        ? rows.length
        : rows.filter((row) => row.getBoundingClientRect().bottom <= bottom - MORE_ROW_HEIGHT).length
    )
  })

  const shown = fit === null ? projects : projects.slice(0, fit)
  const hidden = projects.length - shown.length

  return (
    <div ref={box} className="tv__table-box">
    <table className="tv__table">
      <thead>
        <tr>
          <th style={{ width: compact ? '40%' : '24%' }}>Projekt</th>
          {!compact && <th style={{ width: '12%' }}>Projektleder</th>}
          <th style={{ width: compact ? '22%' : '13%' }}>Status</th>
          <th style={{ width: compact ? '22%' : '14%' }}>Fremdrift</th>
          {!compact && <th style={{ width: '25%' }}>Næste handling</th>}
          <th style={{ width: compact ? '16%' : '12%' }}>Deadline</th>
        </tr>
      </thead>
      <tbody>
        {shown.map((project) => (
          <ProjectRow key={project.id} project={project} compact={compact} />
        ))}
        {hidden > 0 && (
          <tr className="tv__more-row">
            <td colSpan={columns}>+ {hidden} {hidden === 1 ? 'sag mere' : 'flere sager'}</td>
          </tr>
        )}
        {view && view.projects.length === 0 && (
          <tr>
            <td colSpan={columns} style={{ textAlign: 'center', color: '#94A3B8' }}>
              Ingen projekter matcher dette dashboard.
            </td>
          </tr>
        )}
      </tbody>
    </table>
    </div>
  )
}

/**
 * Så mange medarbejdere er der plads til på en 1080p-skærm ved siden af sagerne -
 * målt i browseren. Uden billeder var grænsen 12; med profilbilledet blev rækkerne
 * højere, den 12. blev skåret over, og linjen med "+ N flere" forsvandt.
 */
const MAX_STAFFING_ROWS = 11

/**
 * Dagens bemanding (beslutning 076): hvem er hvor i dag, og hvem har fri.
 *
 * Kun planlagte sager, servicebesøg og fravær. Opgaver og næste handlinger er for meget
 * at læse på afstand - de står i kalenderen. De, der er ude, står øverst; dem uden noget
 * planlagt står nederst og dæmpet, så skærmen svarer på "hvor er folk?" først.
 */
function StaffingPanel({ schedule }: { schedule: DaySchedule }) {
  const busy = (row: ScheduleRow) => row.assignments.length > 0
  const absent = (row: ScheduleRow) => (row.absences?.length ?? 0) > 0

  const rows = [
    ...schedule.rows.filter(busy),
    ...schedule.rows.filter((row) => !busy(row) && absent(row)),
    ...schedule.rows.filter((row) => !busy(row) && !absent(row))
  ]
  const shown = rows.slice(0, MAX_STAFFING_ROWS)
  const hidden = rows.length - shown.length

  // Middag, så datoen ikke kan glide en dag på grund af tidszonen.
  const date = new Date(`${schedule.date}T12:00:00`)

  return (
    <section className="tv__staffing">
      <div className="tv__staffing-title">
        I dag · {date.toLocaleDateString('da-DK', { weekday: 'long', day: 'numeric', month: 'long' })}
      </div>

      {shown.map((row) => (
        <StaffingRow key={row.user.id} row={row} />
      ))}

      {hidden > 0 && <div className="tv__staffing-more">+ {hidden} flere</div>}
      {rows.length === 0 && <div className="tv__staffing-none">Ingen medarbejdere at vise.</div>}
    </section>
  )
}

function StaffingRow({ row }: { row: ScheduleRow }) {
  const absences = row.absences ?? []
  const idle = row.assignments.length === 0 && absences.length === 0

  return (
    <div className={`tv__staffing-row${idle ? ' tv__staffing-row--idle' : ''}`}>
      <div className="tv__staffing-person">
        {/* Profilbilledet - initialerne, hvis der ikke er et (beslutning 076). */}
        <Avatar user={row.user} size={40} />
        <span className="tv__staffing-name">{row.user.displayName}</span>
      </div>
      <div className="tv__staffing-items">
        {absences.map((absence) => (
          <span
            key={`fravaer-${absence.id}`}
            className="tv__pill tv__pill--small"
            style={{ background: withAlpha(absence.color, 0.2), color: absence.color }}
          >
            {absence.typeName}
          </span>
        ))}
        {row.assignments.map((assignment) => (
          <span key={assignment.id} className="tv__staffing-item">
            <span className="tv__staffing-dot" style={{ background: assignment.projectStatusColor }} />
            <span className="tv__staffing-text">{assignment.projectName}</span>
            {assignment.startTime && (
              <span className="tv__staffing-time">
                {assignment.startTime}
                {assignment.endTime ? `-${assignment.endTime}` : ''}
              </span>
            )}
          </span>
        ))}
        {idle && <span className="tv__staffing-none">Ikke planlagt</span>}
      </div>
    </div>
  )
}

function ProjectRow({ project, compact = false }: { project: Project; compact?: boolean }) {
  const isOverdue =
    !!project.deadline && new Date(project.deadline) < new Date() && !project.status.isCompleted

  const rowClass = isOverdue ? 'tv__row--overdue' : project.status.isWaiting ? 'tv__row--waiting' : ''

  return (
    <tr className={rowClass} data-project={project.id}>
      <td>
        <div className="tv__project-name">{project.name}</div>
        {project.customer && <div style={{ color: '#94A3B8', fontSize: 19 }}>{project.customer}</div>}
      </td>
      {!compact && <td>{project.projectManager?.displayName ?? '-'}</td>}
      <td>
        <span
          className="tv__pill"
          style={{ background: withAlpha(project.status.color, 0.18), color: project.status.color }}
        >
          {project.status.name}
        </span>
        {project.status.isWaiting && project.waitingReason && (
          <div style={{ color: '#FDBA74', fontSize: 18, marginTop: 4 }}>{project.waitingReason}</div>
        )}
      </td>
      <td>
        <div className="row" style={{ gap: 12 }}>
          <div className="tv__progress" style={{ flex: 1 }}>
            <div
              className="tv__progress-bar"
              style={{ width: `${project.progressPercent}%`, background: progressColor(project.progressPercent) }}
            />
          </div>
          <span
            style={{ minWidth: 62, textAlign: 'right', fontWeight: 650, color: progressColor(project.progressPercent) }}
          >
            {project.progressPercent}%
          </span>
        </div>
      </td>
      {!compact && (
        <td>
          <div>{project.nextActionText ?? '-'}</div>
          {project.nextActionUser && (
            <div style={{ color: '#94A3B8', fontSize: 19 }}>{project.nextActionUser.displayName}</div>
          )}
        </td>
      )}
      <td style={{ color: isOverdue ? '#FCA5A5' : undefined, fontWeight: isOverdue ? 700 : 500 }}>
        {project.deadline ? formatShortDate(project.deadline) : '-'}
      </td>
    </tr>
  )
}

function Stat({ value, label, color }: { value: number; label: string; color?: string }) {
  return (
    <div className="tv__stat">
      <div className="tv__stat-value" style={{ color }}>
        {value}
      </div>
      <div className="tv__stat-label">{label}</div>
    </div>
  )
}

/** Uret i toppen. Opdateres hvert 10. sekund - det er rigeligt for en TV-visning. */
function useClock() {
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 10_000)
    return () => window.clearInterval(timer)
  }, [])

  return now.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })
}

const weekdayFormatter = new Intl.DateTimeFormat('da-DK', { weekday: 'short', timeZone: 'Europe/Copenhagen' })

function WeekSummaryStrip({ summary }: { summary: NonNullable<DashboardView['weekSummary']> }) {
  const numbers = [
    `${summary.projectsCompleted} ${summary.projectsCompleted === 1 ? 'sag' : 'sager'} afsluttet`,
    `${summary.serviceProjectsCompleted} ${summary.serviceProjectsCompleted === 1 ? 'servicesag' : 'servicesager'}`,
    `${summary.serviceVisitsCompleted} servicebesøg`,
    `${summary.projectsCreated} ${summary.projectsCreated === 1 ? 'ny sag' : 'nye sager'}`
  ]
  const next = [
    ...(summary.nextOnCall ? [`📟 ${summary.nextOnCall}`] : []),
    ...summary.nextWeek.map((i) => `${i.title} ${weekdayFormatter.format(new Date(i.at))}${i.allDay ? '' : ` ${formatTime(i.at)}`}`)
  ]

  return (
    <div className="tv__tip tv__week">
      <span className="tv__tip-label tv__week-label">📰 Ugen der gik · uge {summary.weekNumber}</span>
      <strong>{numbers.join(' · ')}</strong>
      {next.length > 0 && <span className="tv__tip-body">Næste uge: {next.join(' · ')}</span>}
    </div>
  )
}
